import { ArrowRight, Check, RefreshCw } from "lucide-react";

// Presentation-only status, not a backend enum. "active" is reserved for the
// pre-existing cart-flow success screen (reached via MercadoPago redirect,
// out of scope here) — it is never entered by the redeem flow. "prepared" is
// the redeem flow's confirmed terminal state: company row provisioned, which
// is NOT the same signal as Cognito account activation (a separate async
// job) — so "prepared" must never claim an active account, a welcome email,
// or an invoice, only that the company is ready and the login link works
// (SE-159ONB-READY corrective 1).
export type RedeemStatus = "active" | "prepared" | "pending" | "unconfirmed" | "failed";

export function Success({
  name,
  email,
  loginUrl,
  status = "active",
  checking = false,
  onRetryStatus,
}: {
  name: string;
  email: string;
  loginUrl: string;
  status?: RedeemStatus;
  checking?: boolean;
  onRetryStatus?: () => void;
}) {
  const firstName = (name || "").trim().split(" ")[0] || "todo";

  if (status === "prepared") {
    return (
      <div className="screen success">
        <div className="success__check">
          <Check size={40} strokeWidth={2.4} />
        </div>
        <h1 className="h1" style={{ marginTop: 22 }}>
          Tu empresa está preparada
        </h1>
        <p className="lede" style={{ marginTop: 10 }}>
          Registramos tu empresa correctamente. Usa el siguiente enlace para
          iniciar sesión con tu correo y contraseña.
        </p>

        <a
          className="btn btn--primary btn--block"
          href={loginUrl}
          style={{ marginTop: 28 }}
        >
          Ir a Synapta <ArrowRight size={20} />
        </a>

        <div className="success__details">
          <div className="eyebrow">Detalles</div>
          <dl className="dlist">
            <dt>Empresa</dt>
            <dd className="tnum">preparada</dd>
            <dt>Correo</dt>
            <dd>{email || "—"}</dd>
          </dl>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="screen success">
        <h1 className="h1">No pudimos activar tu cuenta</h1>
        <p className="lede" style={{ marginTop: 10 }}>
          Tu código o factura no se pudo procesar. Intenta de nuevo o
          contáctanos si el problema sigue.
        </p>
      </div>
    );
  }

  if (status === "pending" || status === "unconfirmed") {
    return (
      <div className="screen success">
        <h1 className="h1">Estamos preparando tu empresa</h1>
        <p className="lede" style={{ marginTop: 10 }}>
          {status === "pending"
            ? // Redeem accepts a code, a CFDI or a quote folio (ADR-CMRC-013) —
              // not every credential proves a payment, so this copy stays
              // neutral about what was actually recorded.
              "Registramos tu solicitud y estamos preparando tu empresa. Esto puede tardar unos minutos."
            : "Todavía no podemos confirmar que tu empresa esté lista. Puedes intentar revisar de nuevo en un momento."}
        </p>
        {onRetryStatus && (
          <button
            className="btn btn--primary btn--block"
            style={{ marginTop: 28 }}
            onClick={onRetryStatus}
            disabled={checking}
          >
            {checking ? (
              <>
                <RefreshCw size={18} className="spin" /> Revisando…
              </>
            ) : (
              "Revisar estado"
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="screen success">
      <div className="success__check">
        <Check size={40} strokeWidth={2.4} />
      </div>
      <h1 className="h1" style={{ marginTop: 22 }}>
        ¡Listo, {firstName}!
      </h1>
      <p className="lede" style={{ marginTop: 10 }}>
        Te enviamos un correo de bienvenida a <strong>{email || "tu correo"}</strong>.
        Mientras tanto, tu cuenta ya está activa.
      </p>

      <a
        className="btn btn--primary btn--block"
        href={loginUrl}
        style={{ marginTop: 28 }}
      >
        Ir a Synapta <ArrowRight size={20} />
      </a>

      <div className="success__details">
        <div className="eyebrow">Detalles</div>
        <dl className="dlist">
          <dt>Acceso</dt>
          <dd className="tnum">activo</dd>
          <dt>Correo</dt>
          <dd>{email || "—"}</dd>
        </dl>
        <p className="success__fact">
          ¿Necesitas factura? Te llegará por correo en minutos.
        </p>
      </div>
    </div>
  );
}
